/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState } from "react";
import { Card } from "@material-ui/core";
import { getConfigsAsync, updateConfigAsync } from "../services/ConfigService";
import { getContactFlowsAsync } from "../../../sharedServices/AmazonConnectService";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../utility/errorCodes";
import {
	ContentLayout,
	LeftPanelLayout,
	PanelContainer,
	ProgressOverlay,
	RightPanelLayout,
	ShowSnackbar,
} from "../../../components/Common";
import { ConfigurationsList } from "../components/ConfigurationsList";
import ConfigurationDetailsCard from "../components/ConfigurationDetailsCard";
import { useCallback } from "react";

const COMPONENT_NAME = "Admin webChatManager/pages/WebChatConfigurationPage";

const verifyConfig = (config) => {
	return !!(
		config &&
		config.allowedOrigins &&
		config.allowedOrigins.replace(/\s/g, "").length > 0 &&
		config.contactFlowId &&
		config.contactFlowId.length > 0 &&
		config.friendlyName &&
		config.friendlyName.replace(/\s/g, "").length > 0 &&
		(!config.surveysEnabled || (config.surveyQuestions && config.surveyQuestions.length > 0))
	);
};

/**
 * Web Chat customization page.
 *
 * This page is used to customize the web chats look as well as demonstrate give the user a html snippet of using the
 */
const ChatConfigurationsPage = () => {
	const [chatConfigs, setConfigs] = React.useState(undefined);
	const [contactFlows, setContactFlows] = React.useState(undefined);
	const [edited, setEdited] = React.useState(false);
	const [notification, setNotification] = React.useState({ message: "", severity: "success" });
	const [validationMessage, setValidationMessage] = React.useState(undefined);
	const [isLoading, setIsLoading] = useState(false);

	const handleSnackBarClose = () => {
		setNotification((cur) => ({ ...cur, message: "" }));
	};

	const updateChatConfig = async (newConfig, successMessage, failureMessage) => {
		try {
			setIsLoading(true);
			setSelectedConfig(newConfig.config);
			setSelectedConfigId(newConfig.configId);
			await updateConfigAsync({
				updateConfig: newConfig,
			});
			setNotification({ message: successMessage, severity: "success" });
			await getConfigs(false);
			setEdited(false);
		} catch (error) {
			v2.error(`Error updating web chat config`, {
				additionalData: {
					error: errorToObject(error),
					data: { selectedConfig, selectedConfigId },
				},
				logCode: errorCodes.UPDATE_WEBCHAT_CONFIG_ERROR,
				componentName: COMPONENT_NAME,
			});
			setNotification({ message: failureMessage, severity: "error" });
		} finally {
			setIsLoading(false);
		}
	};

	//Set selected config to default to first config in displayed configs on load.
	const [selectedConfig, setSelectedConfig] = React.useState(undefined);
	const [selectedConfigId, setSelectedConfigId] = React.useState(undefined);
	const isValidConfig = verifyConfig(selectedConfig);

	//get web chat configs
	const getConfigs = useCallback(async (resetSelection = true) => {
		setIsLoading(true);
		const newConfigs = await getConfigsAsync();
		setConfigs(newConfigs || []);
		if (resetSelection) {
			setSelectedConfigId(undefined);
			setSelectedConfig(undefined);
		}
		setIsLoading(false);
	}, []);

	const getContactFlows = useCallback(async () => {
		setIsLoading(true);
		const flows = await getContactFlowsAsync();
		setContactFlows(flows);
		setIsLoading(false);
	}, []);

	React.useEffect(() => {
		getConfigs();
		getContactFlows();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleSelectedConfigChange = (newConfig) => {
		setEdited(false);
		setSelectedConfig(newConfig.config);
		setSelectedConfigId(newConfig.configId);
	};

	return (
		<ContentLayout style={{ maxWidth: "100%" }} className="w-100">
			{isLoading && <ProgressOverlay />}
			<ShowSnackbar severity={notification.severity} message={notification.message} onClose={handleSnackBarClose} />
			<PanelContainer>
				<LeftPanelLayout style={{ maxWidth: 408 }} className="w-30">
					<Card style={{ minHeight: 600, height: "100%", padding: "10px 5px" }}>
						<ConfigurationsList
							configurations={chatConfigs}
							addConfigurationHandler={updateChatConfig}
							formEdited={edited}
							selectConfigurationHandler={handleSelectedConfigChange}
							selectedConfiguration={selectedConfig}
							selectedConfigId={selectedConfigId}
						/>
					</Card>
				</LeftPanelLayout>
				<RightPanelLayout className="w-70">
					<Card>
						<ConfigurationDetailsCard
							setIsLoading={setIsLoading}
							chatConfigs={chatConfigs}
							contactFlows={contactFlows}
							edited={edited}
							isCurrentConfigValid={isValidConfig}
							validationMessage={validationMessage}
							selectedConfig={selectedConfig}
							selectedConfigId={selectedConfigId}
							setEdited={setEdited}
							refreshConfigs={getConfigs}
							setSelectedConfig={setSelectedConfig}
							setNotification={setNotification}
							setValidationMessage={setValidationMessage}
							updateChatConfig={updateChatConfig}
						/>
					</Card>
				</RightPanelLayout>
			</PanelContainer>
		</ContentLayout>
	);
};

export default ChatConfigurationsPage;
